import Control from "./Control";
import {AdaptiveDpr, BakeShadows, Sky, useProgress} from "@react-three/drei";
import {useFrame, useThree} from "@react-three/fiber";
import {DefaultXRControllers, Hands, Interactive, useController, useXR, useXREvent, VRCanvas} from '@react-three/xr'
import {Suspense, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Group,
    MeshLambertMaterial,
    Plane,
    SphereGeometry,
    Vector3
} from "three";
import {Note, Position} from "../models/fauna/dbSchema";
import {BuildingStorey, IFCSpace} from "../models/types";
import {toast} from "react-toastify";
import IFCModel from "./IFCModel";
import Model from "./Model";
import {TubePainter} from "three/examples/jsm/misc/TubePainter";

interface ModelCanvasProps {
    notes: any[],
    displayedNote: Note | undefined,
    viewNote: (note: any) => any,
    cameraLook: Position,
    cameraPosition: Position,
    url?: string,
    ext?: string,
    displayedStory?: BuildingStorey,
    showBuildingStories: (buildingStories: BuildingStorey[]) => void,
    showIFCSpaces: (ifcSpaces: IFCSpace[]) => void,
    highlightedIDs: any[],
    addNote?: (note: any) => any,
    setDefaultPosition?: any,
    setDefaultLook?: any,
    displayOneNote: boolean
}

const additionClipping = 1.5;

const cursor = new Vector3();

export default function ModelCanvas({
                                        notes,
                                        displayedNote,
                                        viewNote,
                                        cameraLook,
                                        cameraPosition,
                                        url,
                                        ext,
                                        showBuildingStories,
                                        highlightedIDs,
                                        showIFCSpaces,
                                        displayedStory,
                                        addNote,
                                        setDefaultPosition,
                                        setDefaultLook,
                                        displayOneNote
                                    }: ModelCanvasProps) {

    const clippingHeight = displayedStory ? displayedStory.elevation + additionClipping : undefined;
    const player: Group = useXR((state) => state.player);
    const [vrNote, setVrNote] = useState<any>(null);

    const createNote = useCallback((position: Position, cameraPosition: Position) => {
        return {
            storey: displayedStory,
            position,
            cameraPosition
        }
    }, [displayedStory]);

    const addNoteToModel = useCallback((e: any, addNote: (note: any) => any, camera: any, raycaster: any) => {
        if (clippingHeight === undefined || e.point.y < clippingHeight) {
            e.stopPropagation();
            addNote(createNote({x: e.point.x, y: e.point.y, z: e.point.z}, camera.position));
        } else {
            // Other Raycaster does only report the first hit on IFC models
            const intersected = raycaster.intersectObject(e.eventObject);
            // @ts-ignore does not see that clippingHeight can not be undefined
            const intersectedObj = intersected.find((e: any) => e.point.y < clippingHeight);
            intersectedObj && addNote(createNote({
                x: intersectedObj.point.x,
                y: intersectedObj.point.y,
                z: intersectedObj.point.z
            }, camera.position));


        }
    }, [clippingHeight, createNote]);

    useEffect(() => {
        if (vrNote) {
            player.position.set(vrNote.position.x, vrNote.position.y, vrNote.position.z);
        }
    }, [vrNote, player]);

    useXREvent('squeeze', (event: any) => {
        if (notes && notes.length) {
            const currentInd: number | undefined = notes.indexOf(vrNote);
            setVrNote(notes[currentInd !== undefined ? (currentInd + 1) % notes.length : 0]);
        }
    });

    const directCamera = useCallback((mesh: any) => {
        if (!displayOneNote && mesh && setDefaultPosition && setDefaultLook) {
            mesh.geometry.computeBoundingBox();
            if (isFinite(mesh.geometry.boundingBox.max.x
                    && isFinite(mesh.geometry.boundingBox.max.y))
                && isFinite(mesh.geometry.boundingBox.max.z)
            ) {
                const center = new Vector3();
                const corner = new Vector3(
                    mesh.geometry.boundingBox.max.x / 2,
                    mesh.geometry.boundingBox.max.y,
                    mesh.geometry.boundingBox.max.z
                );
                mesh.geometry.boundingBox.getCenter(center);
                mesh.localToWorld(center);
                mesh.localToWorld(corner);
                setDefaultPosition(corner);
                setDefaultLook(center);
            }
        }
    }, [displayOneNote, setDefaultPosition, setDefaultLook]);

    return <VRCanvas frameloop="demand" performance={{min: 0.5}}>
        <pointLight position={[400, 400, 0]} intensity={0.5} color="white"/>
        <pointLight position={[-400, 400, 0]} intensity={0.3} color="white"/>
        <pointLight position={[0, 400, -400]} intensity={0.4} color="white"/>
        <pointLight position={[0, 400, 400]} intensity={0.2} color="white"/>

        <Sky distance={450000} sunPosition={[0, 1, 0]} inclination={0} azimuth={0.25}/>

        <Control lookAt={cameraLook} position={cameraPosition}/>

        <Painter/>

        <DefaultXRControllers
            /** Optional material props to pass to controllers' ray indicators */
            rayMaterial={{color: 'blue'}}
            /** Whether to hide controllers' rays on blur. Default is `false` */
            hideRaysOnBlur={false}
        />
        <Hands/>
        {!(ext && url) && <gridHelper args={[1000, 1000, "white", "white"]}/>}

        {clippingHeight !== undefined && <ClippingPlane height={clippingHeight}/>}
        <Suspense fallback={<Loader/>}>
            {ext && url ? ext === 'ifc' ?
                    <IFCModel url={url}
                              createNote={(e: any, camera: any, raycaster: any) => !displayOneNote && addNote && addNoteToModel(e, addNote, camera, raycaster)}
                              showBuildingStories={showBuildingStories} showIFCSpaces={showIFCSpaces}
                              directCamera={directCamera} highlightedIDs={highlightedIDs}/>
                    : <Model url={url} ext={ext}
                             createNote={(e: any, camera: any, raycaster: any) => !displayOneNote && addNote && addNoteToModel(e, addNote, camera, raycaster)}
                             directCamera={directCamera}/>
                : null}
            <BakeShadows/>
        </Suspense>
        <Notes notes={notes} selected={displayedNote} viewNote={viewNote} setVrNote={setVrNote}
               clippingHeight={clippingHeight}/>
        <AdaptiveDpr pixelated/>
    </VRCanvas>
}

function Notes({notes, selected, viewNote, setVrNote, clippingHeight}: any) {
    const [hovered, setHovered] = useState(false)
    const [hoveredNote, setHoveredNote] = useState<any>(undefined)
    const [selectedNote, setSelectedNote] = useState<any>(selected)
    const geom = useMemo(() => new SphereGeometry(0.08, 32, 32), []);
    const mat = useMemo(() => new MeshLambertMaterial({
        color: "#ff0000",
        wireframe: false,
        transparent: true,
        opacity: 0.7
    }), []);
    const matHover = useMemo(() => new MeshLambertMaterial({
        color: "#008f27", wireframe: false, transparent: true,
        opacity: 0.7
    }), []);
    const matSelected = useMemo(() => new MeshLambertMaterial({
        color: "#06c041", wireframe: false, transparent: true,
        opacity: 0.7
    }), []);
    const ref = useRef();

    const hover = (state: boolean, note?: any) => {
        setHovered(state);
        setHoveredNote(note);
    }

    useEffect(() => {
        document.body.style.cursor = hovered ? 'pointer' : 'auto'
    }, [hovered]);

    useEffect(() => {
        setSelectedNote(selected)
    }, [selected]);

    // @ts-ignore
    return (<group ref={ref}>
            {notes.map((note: any, index: number) =>
                clippingHeight !== undefined && note.position.y > clippingHeight ?
                    null :
                    <Interactive key={index} onSelect={() => setVrNote(note)} onHover={() => hover(true, note)}
                                 onBlur={() => hover(false)}>
                        <mesh key={index} position={[note.position.x, note.position.y, note.position.z]} geometry={geom}
                              material={selectedNote && note.id === selectedNote.id ? matSelected : hoveredNote && note.id === hoveredNote.id ? matHover : mat}
                              onClick={() => viewNote(note)}
                              onPointerOver={() => hover(true, note)}
                              onPointerOut={() => hover(false)}/>

                    </Interactive>
            )}
        </group>
    )
}

function ClippingPlane({height}: { height: number }) {
    const {gl} = useThree();
    useEffect(() => {
        // Save previous defaults
        const {clippingPlanes, localClippingEnabled} = gl;
        const plane = new Plane(new Vector3(0, height > 0 ? -height : height - 0.001, 0), height > 0 ? 1 : -1);
        gl.clippingPlanes = [plane];
        gl.localClippingEnabled = true;
        // Go back to defaults on unmount
        return () => {
            Object.assign(gl, {clippingPlanes, localClippingEnabled});
        }
    }, [gl, height]);
    return null;
}

function Loader() {
    const toastId = useRef<any>(null);
    const {active, progress, errors} = useProgress();
    useEffect(() => {
        if (!active) {
            toast.done(toastId.current);
            return;
        }
        if (errors.length > 0) {
            for (let i = 0; i < errors.length; i++) {
                toast.error(errors[i]);
            }
        }
        if (toastId.current === null) {
            toastId.current = toast('Loading: ' + Math.round(progress) + '%', {progress: 1 - progress / 100});
        } else {
            if (1 - progress / 100 === 100) {
                toast.done(toastId.current);
                return;
            } else {
                toast.update(toastId.current, {
                    progress: 1 - progress / 100,
                    render: 'Loading: ' + Math.round(progress) + '%'
                });
            }
        }
        return () => toast.done(toastId.current);
    }, [active, progress, errors]);

    return <></>
}

function Painter() {
    const [paintingState, setPaintingState] = useState<any>({isSelecting: false, isSqueezing: false,});
    //const pivot = useMemo(() => new Mesh(new IcosahedronGeometry(0.01, 3)), []);

    const painter = new TubePainter();
    const rightController = useController('right');

    useXREvent('selectstart', () => {
        console.log("Selected")
        setPaintingState({isSelecting: true})
    });

    useXREvent('selectend', () =>
        setPaintingState({isSelecting: false})
    );

    useFrame(() => {
            //rightController && cursor.setFromMatrixPosition(rightController.matrixWorld);
            if (rightController) {
                if (paintingState.isSelecting) {
                    console.log(rightController.children[0].position)
                    // @ts-ignore
                    painter.lineTo(rightController.children[0].position);
                    // @ts-ignore
                    painter.update();
                } else {
                    // @ts-ignore
                    painter.moveTo(rightController.children[0].position);
                }
            }

        }
    )
    return <primitive object={painter.mesh} position={[10, 0, 0]}/>
}